import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useState, useEffect } from "react";
import { gql, useMutation } from "@apollo/client";
import { TeamsQuery } from "~/graphql/__generated__/graphql";

interface Provider {
  id: number;
  firstName: string;
  lastName: string;
  __typename?: string;
}

interface EditProviderTeamDialogProps {
  open: boolean;
  onClose: () => void;
  team: TeamsQuery["team"][number] | null;
  providerSelectItems: Provider[];
  onSuccess?: () => void;
}

const UPDATE_TEAM = gql(`
  mutation UpdateTeam($id: Int!, $name: String!, $providerIds: [Int!])
  @permission(requires: manageTeams) {
    updateTeam(id: $id, name: $name, providerIds: $providerIds) {
      id
      name
    }
  }
`);

const CREATE_TEAM = gql(`
  mutation CreateTeam($name: String!, $providerIds: [Int!])
  @permission(requires: manageTeams) {
    createTeam(name: $name, providerIds: $providerIds) {
      id
      name
    }
  }
`);

const EditProviderTeamDialog = ({
  open,
  onClose,
  team,
  providerSelectItems,
  onSuccess,
}: EditProviderTeamDialogProps) => {
  const [name, setName] = useState("");
  const [selectedProviders, setSelectedProviders] = useState<Provider[]>([]);

  const [updateTeam, { loading: loadingUpdate }] = useMutation(UPDATE_TEAM);
  const [createTeam, { loading: loadingCreate }] = useMutation(CREATE_TEAM);

  const isLoading = loadingUpdate || loadingCreate;

  useEffect(() => {
    if (team) {
      setName(team.name);
      const providers = team.teamProviders
        .map(tp => tp.provider)
        .filter((p): p is NonNullable<typeof p> => p !== null && p !== undefined);
      setSelectedProviders(providers);
    } else {
      setName("");
      setSelectedProviders([]);
    }
  }, [team]);

  const handleClose = () => {
    setName("");
    setSelectedProviders([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const providerIds = selectedProviders.map(p => p.id);

    if (team) {
      // Update existing team
      await updateTeam({
        variables: {
          id: team.id,
          name,
          providerIds,
        },
      });
    } else {
      // Create new team
      await createTeam({
        variables: {
          name,
          providerIds,
        },
      });
    }

    onSuccess?.();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{team ? "Edit Provider Team" : "Create Provider Team"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Team Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isLoading}
        />
        <Autocomplete
          multiple
          options={providerSelectItems}
          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
          value={selectedProviders}
          onChange={(_event, newValue) => {
            setSelectedProviders(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label="Team Members"
              placeholder="Select providers"
              disabled={isLoading}
            />
          )}
          disabled={isLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading || !name.trim()}>
          {team ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProviderTeamDialog; 